import React from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
  Box,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { generateInvoiceData } from "../../../../utils/fakeMemberData";
import { MemberData } from "./ManageMembers";

interface InvoiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  member: MemberData | null;
}

const InvoiceModal = ({ isOpen, onClose, member }: InvoiceModalProps) => {
  if (!member) return null;

  const invoiceData = generateInvoiceData(member);

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="invoice-dialog-title"
    >
      <DialogTitle
        id="invoice-dialog-title"
        className="flex justify-between items-center"
      >
        <Typography variant="h6">
          Invoice {invoiceData.invoiceNumber}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          tabIndex={0}
          onKeyDown={(e) => e.key === "Enter" && onClose()}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="bg-gray-50">
        <Paper className="p-6 mb-6">
          <div className="flex justify-between items-start mb-6">
            <div>
              <Typography variant="h5" className="mb-1">
                {invoiceData.companyName}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Invoice #{invoiceData.invoiceNumber}
              </Typography>
            </div>

            <div className="text-right">
              <Box className="flex items-center bg-green-100 text-green-800 px-3 py-1 rounded-full">
                <CheckCircleIcon fontSize="small" className="mr-1" />
                <Typography variant="body2">{invoiceData.status}</Typography>
              </Box>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div>
              <Typography
                variant="body2"
                color="textSecondary"
                className="mb-1"
              >
                Issue Date
              </Typography>
              <Typography variant="body1">{invoiceData.issueDate}</Typography>
            </div>

            <div>
              <Typography
                variant="body2"
                color="textSecondary"
                className="mb-1"
              >
                Due Date
              </Typography>
              <Typography variant="body1">{invoiceData.dueDate}</Typography>
            </div>

            <div>
              <Typography
                variant="body2"
                color="textSecondary"
                className="mb-1"
              >
                Amount
              </Typography>
              <Typography variant="h6">
                $
                {invoiceData.amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </Typography>
            </div>
          </div>

          <Divider className="mb-4" />

          <Typography variant="h6" className="mb-3">
            Invoice Details
          </Typography>

          <Table size="small" aria-label="invoice items table">
            <TableHead>
              <TableRow>
                <TableCell>
                  <strong>Description</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Quantity</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Unit Price</strong>
                </TableCell>
                <TableCell align="right">
                  <strong>Amount</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invoiceData.items.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{item.description}</TableCell>
                  <TableCell align="right">{item.quantity}</TableCell>
                  <TableCell align="right">
                    $
                    {item.unitPrice.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                  <TableCell align="right">
                    $
                    {item.total.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          <div className="mt-4 bg-gray-50 p-4 rounded-md">
            <div className="flex justify-between">
              <Typography variant="body1">
                <strong>Total</strong>
              </Typography>
              <Typography variant="body1">
                <strong>
                  $
                  {invoiceData.amount.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </strong>
              </Typography>
            </div>
          </div>
        </Paper>
      </DialogContent>
    </Dialog>
  );
};

export default InvoiceModal;
